import { Slider } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { formatManwon } from '../../utils/formatKRW';
import { Scenario } from '../../data/scenarios';
import styles from './ScenarioPage.module.css';

type ScenarioValues = Record<string, number>;

const AVERAGE_PENSION_INCOME = 2_861_091;

function getInitialValues(scenario: Scenario): ScenarioValues {
  return scenario.calculatorFields.reduce<ScenarioValues>((acc, field) => {
    acc[field.name] = field.defaultValue;
    return acc;
  }, {});
}

function calculateScenarioResult(scenario: Scenario, values: ScenarioValues): number {
  switch (scenario.id) {
    case 'pension-at-65': {
      const factor = 0.5 + 0.005 * (values.subscriptionYears - 10);
      return Math.max(0, (0.5 * AVERAGE_PENSION_INCOME + 0.5 * values.monthlyIncome * 10_000) * factor);
    }
    case 'fire-30s': {
      const fireNumber = (values.monthlyExpense * 10_000 * 12) / 0.04;
      let assets = values.currentAssets * 10_000;
      for (let year = 0; year <= 60; year += 1) {
        if (assets >= fireNumber) {
          return values.currentAge + year;
        }
        assets = assets * 1.07 + values.monthlyInvestment * 10_000 * 12;
      }
      return -1;
    }
    case 'retirement-50-billion': {
      const target = values.targetAmount * 10_000;
      const monthlyRate = values.expectedReturn / 100 / 12;
      const months = values.years * 12;
      if (monthlyRate === 0) {
        return target / months;
      }
      return target / (((1 + monthlyRate) ** months - 1) / monthlyRate);
    }
    case 'severance-10years': {
      const monthlySalary = values.monthlySalary * 10_000;
      const annualBonus = values.annualBonus * 10_000;
      const averageDailyWage = (monthlySalary * 3 + (annualBonus * 3) / 12) / 90;
      return averageDailyWage * 30 * 10;
    }
    case 'dividend-300-monthly':
      return (values.targetMonthlyDividend * 10_000 * 12) / 0.038;
    default:
      return 0;
  }
}

function formatFieldValue(value: number, unit: string): string {
  if (unit === '만 원') {
    return `${value.toLocaleString('ko-KR')}만 원`;
  }

  if (unit === '%') {
    return `${value}%`;
  }

  return `${value.toLocaleString('ko-KR')}${unit}`;
}

function formatResult(value: number, unit: string): string {
  if (unit === '세') {
    return value < 0 ? '달성 어려움' : `${Math.round(value)}세`;
  }

  if (unit === '원') {
    return formatManwon(value);
  }

  return `${Math.round(value).toLocaleString('ko-KR')}${unit}`;
}

export interface ScenarioCalculatorProps {
  scenario: Scenario;
}

export function ScenarioCalculator({ scenario }: ScenarioCalculatorProps) {
  const [values, setValues] = useState<ScenarioValues>(() => getInitialValues(scenario));

  useEffect(() => {
    setValues(getInitialValues(scenario));
  }, [scenario]);

  const resultValue = useMemo(() => calculateScenarioResult(scenario, values), [scenario, values]);

  return (
    <div className={styles.calculatorCard}>
      <div className={styles.resultDisplay}>
        <div className={styles.resultAmount}>{formatResult(resultValue, scenario.result.unit)}</div>
        <div className={styles.resultLabel}>{scenario.result.label}</div>
      </div>

      {scenario.calculatorFields.map((field) => (
        <div key={field.name} className={styles.fieldBlock}>
          <div className={styles.fieldHeader}>
            <span className={styles.fieldLabel}>{field.label}</span>
            <span className={styles.fieldValue}>{formatFieldValue(values[field.name], field.unit)}</span>
          </div>
          <Slider
            value={values[field.name]}
            min={field.min}
            max={field.max}
            step={field.step}
            onChange={(_, value) => setValues((current) => ({ ...current, [field.name]: value as number }))}
            sx={{ color: '#000033' }}
          />
        </div>
      ))}
    </div>
  );
}
